import { Box } from '@mui/material';
import { Button, IconButton, Typography, colors } from '@sweep-io/sweep-design';
import { Delete } from '@sweep-io/sweep-design/dist/icons';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '../../../common/dialogs/ConfirmDialog';
import GridLayoutDnd, { GridItem } from '../../../common/GridLayoutDnd';
import { CreditQueueRecord } from '../../../../reducers/assignmentGroupTypes';
import { AssignmentGroupMember } from '../../../../reducers/assignmentGroupTypes';

interface CreditQueueDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (records: CreditQueueRecord[], deletedSfIds: string[]) => Promise<void>;
  creditQueueRecords: CreditQueueRecord[];
  members: AssignmentGroupMember[];
}

interface CreditQueueItemWithName extends CreditQueueRecord {
  userName: string;
}

const CreditQueueItemRow = ({
  item,
  dragHandle,
  onDelete,
}: {
  item: CreditQueueItemWithName;
  dragHandle: ReactNode;
  onDelete: () => void;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderBottom: `1px solid ${colors.grey[120]}`,
        '&:hover': {
          backgroundColor: colors.grey[200],
          '.delete-button': {
            opacity: 1,
          },
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>{dragHandle}</Box>
        <Typography variant="body" color={colors.grey[800]}>
          {item.userName}
        </Typography>
      </Box>
      <Box className="delete-button" sx={{ opacity: 0 }}>
        <IconButton variant="flat" size="tiny" onClick={onDelete}>
          <Delete />
        </IconButton>
      </Box>
    </Box>
  );
};

export const CreditQueueDialog = ({
  isOpen,
  onClose,
  onSave,
  creditQueueRecords,
  members,
}: CreditQueueDialogProps) => {
  const [localRecords, setLocalRecords] = useState<CreditQueueItemWithName[]>([]);
  const [deletedSfIds, setDeletedSfIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const getUserName = useCallback(
    (userId: string) => {
      const member = members.find((m) => m.userId === userId);
      return member?.name ?? 'Unknown User';
    },
    [members],
  );

  useEffect(() => {
    if (isOpen) {
      const recordsWithNames = creditQueueRecords.map((record) => ({
        ...record,
        userName: getUserName(record.userId),
      }));
      setLocalRecords(recordsWithNames);
      setDeletedSfIds([]);
    }
  }, [isOpen, creditQueueRecords, getUserName]);

  const handleReorder = useCallback((reorderedRecords: CreditQueueItemWithName[]) => {
    setLocalRecords(reorderedRecords);
  }, []);

  const handleDelete = useCallback((sfId: string) => {
    setLocalRecords((prev) => prev.filter((r) => r.sfId !== sfId));
    setDeletedSfIds((prev) => [...prev, sfId]);
  }, []);

  const handleClearAll = useCallback(() => {
    const allSfIds = localRecords.map((r) => r.sfId);
    setDeletedSfIds((prev) => [...prev, ...allSfIds]);
    setLocalRecords([]);
  }, [localRecords]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const recordsToSave = localRecords.map(({ userId, sfId }) => ({ userId, sfId }));
      await onSave(recordsToSave, deletedSfIds);
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [localRecords, deletedSfIds, onSave, onClose]);

  const gridItems: GridItem[] = localRecords.map((record) => ({
    id: record.sfId,
    value: record,
    renderFunction: (dragHandle: ReactNode) => (
      <CreditQueueItemRow
        item={record}
        dragHandle={dragHandle}
        onDelete={() => handleDelete(record.sfId)}
      />
    ),
  }));

  const totalCredits = localRecords.length;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      handleClose={onClose}
      onConfirm={handleSave}
      confirmText="Save"
      cancelText="Cancel"
      showCloseButton
      title="Credit Queue"
      titleVariant="h3"
      PaperProps={{
        sx: {
          width: '676px',
          maxHeight: '80vh',
        },
      }}
      dialogContentSx={{
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
      disableConfirmButton={isSaving}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pb: 2,
        }}
      >
        <Typography variant="body" color={colors.grey[700]}>
          {totalCredits} {totalCredits === 1 ? 'credit' : 'credits'}
        </Typography>
        {totalCredits > 0 && (
          <Button variant="flat-blue" size="small" onClick={handleClearAll}>
            Clear all
          </Button>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          border: `1px solid ${colors.grey[200]}`,
          borderRadius: '4px',
          mx: 3,
          mb: 2,
        }}
      >
        {totalCredits === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
            }}
          >
            <Typography variant="body" color={colors.grey[500]}>
              No credits in queue
            </Typography>
          </Box>
        ) : (
          <GridLayoutDnd
            items={gridItems}
            onReorder={handleReorder}
            itemsPerRow={1}
            readonly={false}
          />
        )}
      </Box>
    </ConfirmDialog>
  );
};
